import { trackClient } from '#app/analytics/client.ts'
import { Badge } from '#app/components/ui/badge.tsx'
import { type AdForRender } from '#app/utils/ads.server.ts'
import { cn } from '#app/utils/misc.tsx'

interface AdBannerProps {
	ad: AdForRender
	className?: string
}

/**
 * Ad banner presentation component
 *
 * Renders image or HTML creative with click tracking
 */
export function AdBanner({ ad, className }: AdBannerProps) {
	const handleClick = () => {
		// Track click with PostHog
		trackClient(
			'ad_click',
			{
				ad_id: ad.id,
				ad_name: ad.name,
				location_key: ad.locationKey,
				creative_type: ad.creativeType,
				target_url: ad.url,
			},
			`ad_click:${ad.id}:${Date.now()}`,
		)
	}

	const clickUrl = `/resources/ad-click?adId=${encodeURIComponent(ad.id)}&r=${encodeURIComponent(ad.url)}`

	return (
		<div className={cn('relative rounded-lg border', className)}>
			<a
				href={clickUrl}
				target="_blank"
				rel="noopener noreferrer sponsored"
				onClick={handleClick}
				className="block transition-opacity hover:opacity-90"
			>
				{ad.creativeType === 'image' && ad.creativeUrl ? (
					<img
						src={ad.creativeUrl}
						alt={ad.altText || ad.name}
						className="h-auto w-full rounded-md object-cover"
						loading="lazy"
					/>
				) : ad.creativeType === 'html' && ad.creativeHtml ? (
					<div
						dangerouslySetInnerHTML={{ __html: ad.creativeHtml }}
						className="p-4"
					/>
				) : null}

				{ad.ctaLabel && (
					<div className="bg-muted/50 px-4 py-2 text-center">
						<span className="text-foreground text-sm font-medium">
							{ad.ctaLabel}
						</span>
					</div>
				)}
			</a>

			<div className="absolute -top-3 left-3">
				<Badge variant="secondary" className="text-muted-foreground text-xs">
					Ad
				</Badge>
			</div>
		</div>
	)
}
